<script>
import Vue from 'vue';
// import debounce from 'lodash/debounce';
import isEmpty from 'lodash/isEmpty';
import isNil from 'lodash/isNil';
import cloneDeep from 'lodash/cloneDeep';
import forEach from 'lodash/forEach';
import has from 'lodash/has';
import { defaultPagination } from '@/constants/table';
import { defaultDateRangeOptions, getDate, getDateToString } from '@/utils/date';
import { getValueByPath, orderBy } from './util';
// import debounce from 'lodash.debounce';

const searchType = {
  DATE: 'date',
  EVENT_LEVEL: 'eventlevel',
  EVENT_LEVEL_MULTI: 'eventlevelMulti',
};

const defaultFilterMultiple = false;

export default {
  name: 'ElTableWrapper',
  data() {
    return {
      states: {
        filters: {},
        dateFilters: {},
        sortElTableColumn: null,
        sortColumn: null,
        sortOrder: null,
        searches: {},
        pagination: this.getDefaultPagination(),
      },
    };
  },
  props: {
    /**
     * 테이블 데이터
     */
    data: {
      type: Array,
      default() {
        return [];
      },
    },
    total: {
      type: [Number, Object],
      default: null,
    },
    /**
     * 테이블 컬럼
     */
    columns: {
      type: Array,
      default() {
        return [];
      },
    },
    /**
     * 커스텀 헤더 제공 여부
     * (헤더 검색 기능 사용 시 true)
     */
    showCustomHeader: {
      type: Boolean,
      default: false,
    },
    /**
     * 컬럼 공통 속성
     */
    columnDefault: Object,
    /**
     * 페이징 설정
     */
    pagination: {
      type: [Object, Boolean],
      default: true,
    },
    /**
     * 기본 정렬 prop과 order 설정
     */
    defaultSort: Object,
    /* watchtek custom */
    /**
     * 커스텀 헤더 (헤더 검색) display 여부
     */
    showSearchTool: {
      type: Boolean,
      default: true,
    },
    /**
     * 서버에서 필터링/정렬 처리 여부
     */
    remote: {
      type: Boolean,
      default: false,
    },
    /**
     * 얜 뭐니
     */
    type: {
      type: String,
      default: '',
      validator(value) {
        return value === '' || value === 'popup' || value === 'popup-list';
      },
    },
    /**
     * 지정한 높이에 테이블의 높이가 100%로 설정되도록 합.
     * @type {Object}
     */
    fitHeight: {
      type: Boolean,
    },
    /**
     * 테이블 편집 기능 사용 여부
     * @type {Object}
     */
    editable: {
      type: Boolean,
      default: false,
    },
    cellClassName: {
      type: [Function, String],
    },
  },
  computed: {
    /**
     * 로컬 데이터
     * 1. 정렬
     * 2. 필터링
     * @return {Array}
     * @author Ju
     */
    localData() {
      const states = this.states;
      const dataSource = this.data;
      let data = dataSource || [];
      // 本地排序
      data = data.slice(0);
      if (this.remote !== true) {
        data = this.sortData(data);
      }

      if (this.remote !== true) {
        // 키워드
        if (states.filters) {
          Object.keys(states.filters).forEach((columnKey) => {
            const col = this.findColumn(columnKey, true);
            if (!col || col.searchType === searchType.DATE) {
              return;
            }
            const values = states.filters[columnKey] || [];
            if (values.length === 0) {
              return;
            }

            const filterable = !isEmpty(col.filters);
            let filterMethod;
            if (filterable) {
              filterMethod = col.filterMethod || this.getDefaultFilterMethod(col);
            } else if (col.searchable) {
              filterMethod = col.searchMethod || this.getDefaultSearchMethod(col);
            }

            data = filterMethod ? data.filter((record) => values.some((v) => filterMethod(v, record))) : data; // lint fix
          });
        }

        // 날짜
        if (states.dateFilters) {
          Object.keys(states.dateFilters).forEach((columnKey) => {
            const col = this.findColumn(columnKey, true);
            if (!col) {
              return;
            }
            const filterValue = states.dateFilters[columnKey] || [];

            if (isEmpty(filterValue)) {
              return;
            }
            data = data.filter((record) => this.filterDate(record, col.prop, filterValue));
          });
        }
        this.$emit('load-local-data', data, states);
      }
      return data;
    },
    /**
     * 현재 페이지 데이터
     * 1. 페이징 처리
     * @return {Array}
     * @author Ju
     */
    currentPageData() {
      let data = this.localData;
      const pagination = this.states.pagination;
      // 如果没有分页的话，默认全部展示
      if (!this.hasPagination() || this.remote === true || this.remote === true) {
        return data;
      }

      // 分页
      // ---
      // 当数据量少于等于每页数量时，直接设置数据
      // 否则进行读取分页数据
      const currentPage = this.getMaxCurrent(pagination.total || data.length);
      const pageSize = pagination.pageSize;
      if (data.length > pageSize) {
        data = data.slice((currentPage - 1) * pageSize, currentPage * pageSize);
      }
      return data;
    },
  },
  watch: {
    /**
     * 페이징 설정 변경 감지
     */
    pagination: {
      handler(val) {
        if (val === false) {
          this.states.pagination = {};
          return;
        }
        this.states.pagination = {
          ...defaultPagination,
          ...this.states.pagination,
          ...val,
          currentPage: val.currentPage || 1,
          pageSize: val.pageSize || 10,
        };
      },
      deep: true,
    },
    total: {
      handler(to) {
        if (!isNil(to)) {
          this.states.pagination = {
            ...defaultPagination,
            ...this.states.pagination,
            total: to,
          };
          this.$emit('total-change', this.states.pagination);
        }
      },
    },
  },
  created() {},
  mounted() {
    /* eslint-disable array-callback-return, no-unused-vars */
    this.columns.map((columnAttr, i) => {
      if (columnAttr.filters && columnAttr.filteredValue) {
        const values = columnAttr.filteredValue || [];
        const key = this.getColumnKey(columnAttr);
        this.states.filters[key] = values;
      }
    });
    /* eslint-enable array-callback-return, no-unused-vars */

    if (this.defaulSort) {
      this.states.sortColumn = this.findColumnByProp(this.defaulSort.prop);
      this.states.sortOrder = this.defaulSort.order || 'ascending';
    }
  },
  methods: {
    /**
     * 컬럼을 렌더링 한다.
     * @author Ju
     * @param  {Object} columnProps 컬럼 속성
     * @param  {Object} tableProps  테이블 속성
     * @return {Element} 컬럼 Element
     */
    renderColumn(columnProps, tableProps) {
      const propsNoCustom = { ...columnProps };
      if (propsNoCustom.custom) {
        delete propsNoCustom.custom;
      }

      if (tableProps.showCustomHeader) {
        propsNoCustom.renderHeader = this.getRenderHeaderFn(columnProps);
      }

      if (columnProps.searchable && columnProps.searchable === true) {
        propsNoCustom.filterMethod = null;
        propsNoCustom.filters = null;
        delete propsNoCustom.searchable;
      }
      if (columnProps.filterMethod) {
        propsNoCustom.filterMethod = null;
      }
      if (columnProps.sortable) {
        propsNoCustom.sortable = 'custom';
      }
      if (columnProps.scopedSlot) {
        delete propsNoCustom.scopedSlot;
      }
      if (columnProps.type && columnProps.type === 'index') {
        propsNoCustom.className = propsNoCustom.className || '';
        propsNoCustom.className += ' ll-index';
      }

      // 하위 컬럼이 있는 경우 하위 컬럼 Element를 생성하여 배열에 넣는다.
      const columnChildren = [];
      if (columnProps && columnProps.childColumns) {
        const childColumns = columnProps.childColumns;
        for (let i = 0; i < childColumns.length; i += 1) {
          const data = this.renderColumn(childColumns[i], tableProps);
          columnChildren.push(data);
        }
      }

      /* eslint-disable no-nested-ternary */
      return (
        <el-table-column
          {...{
            props: propsNoCustom,
          }}
        >
          {columnProps && columnProps.scopedSlot ? this.$scopedSlots[columnProps.scopedSlot] : columnChildren.length > 0 ? columnChildren : ''}
        </el-table-column>
      );
      /* eslint-enable no-nested-ternary */
    },
    /**
     * 헤더 컨텐츠에 검색 컴포넌트를 렌더링한다.
     * @author Ju
     * @param  {[type]} h          [description]
     * @param  {Object} columnAttr 컬럼 속성
     * @param  {Object} column     컬럼
     * @return {Element}           헤더 컨텐츠 검색 컴포넌트 Element
     */
    renderHeaderContentSearch(h, columnAttr, column) {
      const that = this;
      const key = this.getColumnKey(columnAttr);
      if (!this.states.searches[key]) {
        Vue.set(this.states.searches, key, '');
      }
      const value = this.states.searches[key] || '';

      if (columnAttr.searchType === searchType.DATE) {
        return (
          <el-date-picker
            class="header-content-filter"
            v-model={ this.states.searches[key] }
            type="daterange"
            editable={ false }
            clearable={ true }
            unlinkPanels={ true }
            format={ defaultDateRangeOptions.format }
            value-format={ defaultDateRangeOptions.valueFormat }
            defaultTime={ defaultDateRangeOptions.defaultTime }
            start-placeholder={ defaultDateRangeOptions.startPlaceholder }
            end-placeholder={ defaultDateRangeOptions.endPlaceholder }
            picker-options={ defaultDateRangeOptions.pickerOptions }
            range-separator={ defaultDateRangeOptions.rangeSeparator }
            onChange={ (pickerValue) => { this.onDateFilterChange(pickerValue, columnAttr); } }
          >
            {value && (
              <i
                class="el-input__icon el-icon-close icon-search-delete"
                {...{
                  slot: 'suffix',
                  on: {
                    click: (e) => that.onDateClearClick(e, columnAttr),
                  },
                }}
              />
            )}
          </el-date-picker>
        );
      } else if (columnAttr.searchType === searchType.EVENT_LEVEL) {
        return (
          <w-eventlevel-combo
            class="header-content-filter"
            v-model={ this.states.filters[key] }
            onChange={ (eventlevel) => { this.onFilterChange(columnAttr, eventlevel); } }
          />
        );
        /**
         * 2019. 11. 06. (SK) 다중 이벤트 등급 검색 필터링 기능 요청
         * searchType = 'eventlevelMulti' 일경우 아래와 같이 스타일 및 props를 전달하여 multiple select 컴포넌트 제공
         * @author Song
         */
      } else if (columnAttr.searchType === searchType.EVENT_LEVEL_MULTI) {
        return (
          <w-eventlevel-combo style="display: flex; text-align: left;" multipleFlag={ true }
            class="header-content-filter"
            v-model={ this.states.filters[key] }
            onChange={ (eventlevel) => { this.onFilterChange(columnAttr, eventlevel); } }
          />
        );
      }

      return (
        <el-input
          class="header-content-search"
          {...{
            props: {
              value,
            },
            on: {
              // NOTE: debounce로 할 경우 searches에 입력한 키워드 일부가 누락되는 경우 있어 뺌. onSearchInput에서 onSearchChange 호출부에 걸어야될듯.
              // 이렇게 처리하면 onSearchEnterPress에서 searches 설정하는건 의미가 없어 보임.
              // input: debounce((value) => {
              //   that.onSearchInput(columnAttr, value);
              // }, 300),
              input(value) {
                that.onSearchInput(columnAttr, value);
              },
            },
            nativeOn: {
              keyup: (e) => {
                // if (e.target !== e.currentTarget) {
                //   return
                // }
                if (e.keyCode === 13) {
                  // Enter
                  that.onSearchEnterPress(e, columnAttr);
                }
              },
            },
          }}
        >
          {value && (
            <i
              class="el-input__icon el-icon-close icon-search-delete"
              {...{
                slot: 'suffix',
                on: {
                  click: (e) => that.onSearchClearClick(e, columnAttr),
                },
              }}
            />
          )}
        </el-input>
      );
    },
    /* eslint-enable no-unused-vars, no-shadow */
    /* eslint-disable no-unused-vars, arrow-body-style */
    renderHeaderContentFilter(h, columnAttr, column) {
      const that = this;
      const filters = columnAttr.filters;
      const key = this.getColumnKey(columnAttr);
      if (!this.states.filters[key]) {
        Vue.set(this.states.filters, key, undefined);
      }
      const isMultiple = this.isFilterMultiple(columnAttr);
      let filterValue = this.states.filters[key];
      if (filterValue && !isMultiple) {
        filterValue = filterValue.length > 0 ? filterValue[0] : undefined;
      }

      return (
        <el-select
          class="header-content-filter"
          {...{
            props: {
              value: filterValue,
              placeholder: columnAttr.filterPlaceholder,
              multiple: isMultiple,
              clearable: true,
            },
            on: {
              input: (value) => {
                that.states.filters[key] = value;
                that.onFilterChange(columnAttr, value);
              },
              clear: () => {
                that.states.filters[key] = undefined;
                that.onFilterChange(columnAttr, undefined);
              },
            },
          }}
        >
          {filters &&
            filters.map((filter, i) => {
              return (
                <el-option
                  {...{
                    props: {
                      label: filter.text,
                      value: filter.value,
                    },
                  }}
                />
              );
            })}
        </el-select>
      );
    },
    /* eslint-enable no-unused-vars, arrow-body-style */
    renderHeaderContentFilterAndSearch() {},
    /**
     * 컬럼 헤더 컨텐츠 영역 (검색)을 렌더링한다.
     * @author Ju
     * @param  {[type]} h          [description]
     * @param  {Object} columnAttr 컬럼 속성
     * @param  {Ojbect} column     컬럼 객체
     * @param  {[type]} $index     [description]
     * @return {Element}           컬럼 헤더 컨텐츠 Element
     */
    renderHeaderContent(h, columnAttr, column, $index) {
      if (columnAttr.custom && columnAttr.custom.renderHeaderContent) {
        return columnAttr.custom.renderHeaderContent(h, { column, $index });
      }

      if (columnAttr.searchable) {
        return this.renderHeaderContentSearch(h, columnAttr, column);
      }

      const filterable = columnAttr.filters;
      if (filterable) {
        return this.renderHeaderContentFilter(h, columnAttr, column);
      }

      return '';
    },
    renderPagination() {
      if (!this.hasPagination()) {
        return null;
      }
      const { pagination } = this.states;
      const total = pagination.total || this.localData.length;
      if (!(total > 0)) {
        return null;
      }
      return (
        <el-pagination
          class="ll-table-pagination"
          ref="li-pagination"
          background
          {...{
            props: {
              ...pagination,
              total,
              currentPage: this.getMaxCurrent(total),
            },
            on: {
              'size-change': this.onPageSizeChange,
              'current-change': this.onPageCurrentChange,
            },
          }}
        />
      );
    },
    /**
     * 검색 필드 초기화 아이콘 클릭 이벤트 핸들러
     * @author Ju
     * @param  {[type]} event      [description]
     * @param  {[type]} columnAttr [description]
     * @return {[type]}            [description]
     */
    onSearchClearClick(event, columnAttr) {
      event.stopPropagation();
      const key = this.getColumnKey(columnAttr);
      this.states.searches[key] = '';
      // delete this.states.filters[key]; // NOTE: custom add onSearchChange에서 value가 빈 값이 아닌 경우에만 filters에 추가하도록 처리했으므로 설정한 컬럼의 조건 필터를 제거.
      this.onSearchChange(columnAttr, '');
    },
    /**
     * 검색 필드 엔터 키 입력 이벤트 핸들러
     * @author Ju
     * @param  {[type]} event      [description]
     * @param  {[type]} columnAttr [description]
     * @return {[type]}            [description]
     */
    onSearchEnterPress(event, columnAttr) {
      event.stopPropagation();
      event.preventDefault();
      const key = this.getColumnKey(columnAttr);
      // const searchValue = this.states.searches[key];
      // debounce로 인해 keyup 이벤트가 먼저 발생할 경우 입력 값 전체가 searches[key]에 설정되지 않는 문제있음. enter시에도 직접 설정해주도록 함.
      // TODO: input과 동시에 설정될 때 문제없는 지 확인 필요.
      // // if (value === '' && this.states.filters[key]) {
      //   delete this.states.filters[key];
      // }
      const searchValue = event.target.value;
      this.states.searches[key] = searchValue;
      this.onSearchChange(columnAttr, searchValue);
    },
    /**
     * 검색 필드 입력 이벤트 핸들러
     * @author Ju
     * @param  {[type]} columnAttr [description]
     * @param  {[type]} value      [description]
     * @return {[type]}            [description]
     */
    onSearchInput(columnAttr, value) {
      const key = this.getColumnKey(columnAttr);
      this.states.searches[key] = value;
      // if (value === '' && this.states.filters[key]) {
      //   delete this.states.filters[key];
      // }
      if (columnAttr.searchOnInput) {
        this.onSearchChange(columnAttr, value);
      }
    },
    /**
     * 검색 조건 변경 이벤트 핸들러
     * @author Ju
     * @param  {[type]} columnAttr [description]
     * @param  {[type]} value      [description]
     * @return {[type]}            [description]
     */
    onSearchChange(columnAttr, value) {
      let filters;
      if (columnAttr.searchable && columnAttr.searchable === true) {
        const searchFilters = this.getSearchFilters();
        const currentFilters = this.getFiltersExceptEmpty();
        filters = {
          ...currentFilters,
          ...searchFilters,
        };
      } else {
        filters = this.states.filters;
      }

      const dateFilters = this.states.dateFilters;
      this.changeSearchFilter(filters, dateFilters);

      this.$emit('search-change', {
        columnAttr,
        prop: columnAttr.prop,
        value,
      });
    },
    /**
     * 검색 콤보박스 값 변경 이벤트 핸들러
     * @author Ju
     * @param  {[type]} columnAttr   [description]
     * @param  {[type]} filterdValue [description]
     * @return {[type]}              [description]
     */
    onFilterChange(columnAttr, filterdValue) {
      let values = [];
      let filter;
      const key = this.getColumnKey(columnAttr);
      if (filterdValue !== undefined) {
        values = Array.isArray(filterdValue) ? filterdValue : [filterdValue];
        filter = { [key]: values };
      } else {
        // delete this.states.filters[key]; // NOTE: custom add. clear filter
        filter = {};
      }

      this.onTableFilterChange(filter);
    },
    /**
     * 테이블 필터 변경 이벤트 핸들러
     * @author Ju
     * @param  {[type]} filters [description]
     * @return {[type]}         [description]
     */
    onTableFilterChange(filters) {
      const searchFilters = this.getSearchFilters();
      const currentFilters = this.getFiltersExceptEmpty();
      const nextFilters = {
        ...currentFilters,
        ...searchFilters,
        ...filters,
      };
      this.changeSearchFilter(nextFilters);
      this.$emit('filter-change', filters);
    },
    /**
     * 날짜 필터 변경 이벤트 핸들러
     * @author Ju
     * @param  {Array} value      Date Picker 선택 값
     * @param  {[type]} columnAttr [description]
     * @return {[type]}            [description]
     */
    onDateFilterChange(value, columnAttr) {
      const key = this.getColumnKey(columnAttr);
      const dateFilters = cloneDeep(this.states.dateFilters);

      if (isEmpty(value)) {
        delete dateFilters[key];
      } else {
        const start = value[0];
        const end = value[1];

        dateFilters[key] = { start, end };
      }

      const filters = {
        ...this.getFiltersExceptEmpty(),
        ...this.getSearchFilters(),
      };

      this.changeSearchFilter(filters, dateFilters);
    },
    /**
     * 날짜 필터 초기화 버튼 클릭 이벤트 핸들러
     * @author Ju
     * @param  {Object} columnAttr 컬럼 속성
     */
    onDateClearClick(columnAttr) {
      const key = this.getColumnKey(columnAttr);
      const dateFilters = cloneDeep(this.states.dateFilters);
      delete dateFilters[key];

      this.states.dateFilters = dateFilters;
      this.changeSearchFilter();
    },
    onSortClick(event, { columnAttr, order, column }) {
      if (event !== null) {
        event.stopPropagation();
      }

      let { sortColumn, sortOrder, sortElTableColumn } = this.states;
      // 只同时允许一列进行排序，否则会导致排序顺序的逻辑问题
      const isSortColumn = this.isSortColumn(columnAttr);
      if (!isSortColumn) {
        // 当前列未排序
        if (!isEmpty(this.states.sortElTableColumn)) {
          this.states.sortElTableColumn.order = undefined;
        }
        sortColumn = columnAttr;
        sortElTableColumn = column;
        sortOrder = order;
      } else {
        // 当前列已排序
        /* eslint-disable no-lonely-if */
        if (sortOrder === order) {
          sortOrder = '';
          sortColumn = null;
          sortElTableColumn = null;
        } else {
          // 切换为排序状态
          sortOrder = order;
        }
        /* eslint-enable no-lonely-if */
      }

      this.states.sortOrder = sortOrder;
      this.states.sortColumn = sortColumn;
      this.states.sortElTableColumn = sortElTableColumn;

      // TODO: 此处依赖el-table实现底层，有待废除
      if (column !== null) {
        column.order = sortOrder;
      }

      this.$emit('sort-change', {
        column,
        columnAttr: sortColumn,
        prop: sortColumn ? sortColumn.prop : null,
        order: sortOrder,
      });

      this.emitConditionChange();
    },
    onTableSortChange({ column, order }) {
      const columnAttr = column ? this.findColumn(column.columnKey || column.property, false) : null;
      this.onSortClick(null, { columnAttr, column, order });
    },
    /**
     * 페이지 사이즈 변경 이벤트 핸들러
     * @author Ju
     * @param  {[type]} size [description]
     * @return {[type]}      [description]
     */
    onPageSizeChange(size) {
      const pagination = this.states.pagination;
      if (pagination.onSizeChange) {
        pagination.onSizeChange(size);
      }
      const nextPagination = {
        ...pagination,
        currentPage: 1,
        pageSize: size,
      };
      this.states.pagination = nextPagination;
      this.$emit('pagination-change', nextPagination);

      this.emitConditionChange();
    },
    /**
     * 현재 페이지 변경 이벤트 핸들러
     * @author Ju
     * @param  {[type]} currentPage [description]
     * @return {[type]}             [description]
     */
    onPageCurrentChange(currentPage) {
      const pagination = this.states.pagination;
      if (pagination.onCurrentChange) {
        pagination.onCurrentChange(currentPage);
      }
      const nextPagination = {
        ...pagination,
        currentPage: currentPage || pagination.currentPage || 1,
      };
      // Controlled current prop will not respond user interaction NOTE: Ju 수동 페이지 전환은 setCurrentPage로 처리
      // if (this.pagination && typeof this.pagination === 'object' && 'currentPage' in this.pagination) {
      //   nextPagination.currentPage = pagination.currentPage;
      // }
      this.states.pagination = nextPagination;
      this.$emit('pagination-change', nextPagination);

      this.emitConditionChange();
    },
    onHeaderTitleClick(e, { columnAttr, column }) {
      const filterable = columnAttr.filters;
      if ((filterable && this.showCustomHeader) || columnAttr.sortable) {
        e.stopPropagation();
        if (columnAttr.sortable) {
          this.handlerColumnSort(e, { columnAttr, column });
        }
      }
    },
    handlerColumnSort(e, { columnAttr, column }) {
      const currentOrder = column.order;
      let columnAttrParam = columnAttr;
      let columnParam = column;
      let order;
      if (isEmpty(currentOrder)) {
        order = 'ascending';
      } else if (currentOrder === 'ascending') {
        order = 'descending';
      } else {
        order = null;
        columnAttrParam = null;
        columnParam = null;
      }
      this.onSortClick(e, { columnAttr: columnAttrParam, order, column: columnParam });
      e.stopPropagation();
    },
    onHeaderContentClick(e) {
      e.stopPropagation();
    },
    getDefaultPagination() {
      /* eslint-disable indent */
      const pagination = this.pagination || {};
      return this.hasPagination()
        ? {
            ...defaultPagination,
            ...pagination,
          }
        : {};
      /* eslint-enable indent */
    },
    hasPagination() {
      return this.pagination !== false;
    },
    sortData(data) {
      const states = this.states;
      const { sortColumn, sortOrder } = states;
      if (!sortColumn || typeof sortColumn.sortable === 'string') {
        return data;
      }
      const sortProp = isNil(sortColumn.sortProp) ? sortColumn.prop : sortColumn.sortProp;
      return orderBy(data, sortProp, sortOrder, sortColumn.sortMethod);
    },
    getDefaultSearchMethod(columnAttr) {
      const prop = columnAttr.prop;
      const treeProps = this.$attrs['tree-props'];
      const children = !isEmpty(treeProps) ? treeProps.children : null;
      /* eslint-disable func-names, space-before-function-paren */
      return function(value, row) {
        let elementValue = prop && prop.indexOf('.') === -1 ? row[prop] : getValueByPath(row, prop);
        if (typeof elementValue !== 'number' && isEmpty(elementValue)) {
          elementValue = '';
        }
        const elementValueStr = elementValue.toString().toLowerCase();
        const valueStr = value.toString().toLowerCase();
        // #56514 SKT 인프라 요청사항인 tree-grid에서 childList도 검색하기 위해 추가된 로직
        const childList = row[children];
        if (!isEmpty(childList)) {
          if (elementValueStr.indexOf(valueStr) > -1) {
            return true;
          }
          let flag = false;
          childList.forEach((childRow) => {
            let childElementValue = prop && prop.indexOf('.') === -1 ? childRow[prop] : getValueByPath(childRow, prop);
            if (typeof childElementValue !== 'number' && isEmpty(childElementValue)) {
              childElementValue = '';
            }
            const childElementValueStr = childElementValue.toString().toLowerCase();
            if (childElementValueStr.indexOf(valueStr) > -1) {
              flag = true;
            }
          });
          return flag;
        }
        return elementValueStr.indexOf(valueStr) > -1;
      };
      /* eslint-enable func-names, space-before-function-paren */
    },
    getDefaultFilterMethod(columnAttr) {
      // 필터의 경우 value가 null인 값도 존재하기 때문에 null도 빈 값으로 보지 않음.
      // 필터에서 빈 값은 unedined 기준으로만 판단하도록 처리
      const prop = columnAttr.prop;
      /* eslint-disable func-names, space-before-function-paren */
      return function(value, row) {
        let elementValue = prop && prop.indexOf('.') === -1 ? row[prop] : getValueByPath(row, prop);

        if (value === null) {
          return elementValue === null;
        }

        if (typeof elementValue !== 'number' && isEmpty(elementValue)) {
          elementValue = '';
        }
        const elementValueStr = elementValue.toString();
        const valueStr = value.toString();
        return elementValueStr.indexOf(valueStr) > -1;
      };
      /* eslint-enable func-names, space-before-function-paren */
    },
    getMaxCurrent(total) {
      const { currentPage, pageSize } = this.states.pagination;
      if ((currentPage - 1) * pageSize >= total) {
        return Math.floor((total - 1) / pageSize) + 1;
      }
      return currentPage;
    },
    findColumn(myKey, searchable) {
      if (!myKey) {
        return null;
      }

      let column;
      /* eslint-disable array-callback-return */
      if (searchable) {
        this.columns.map((columnAttr) => {
          // 헤더 검색 기능을 사용하는 컬럼만 가져오도록 한다.
          if ((columnAttr.searchable || !isEmpty(columnAttr.filters)) && this.getColumnKey(columnAttr) === myKey) {
            column = columnAttr;
          }

          if (!column && !isEmpty(columnAttr.childColumns)) {
            columnAttr.childColumns.map((childAttr) => {
              if ((childAttr.searchable || !isEmpty(childAttr.filters)) && this.getColumnKey(childAttr) === myKey) {
                column = childAttr;
              }
            });
          }
        });
      } else {
        this.columns.map((columnAttr) => {
          // 헤더 검색 기능을 사용하는 컬럼만 가져오도록 한다.
          if (this.getColumnKey(columnAttr) === myKey) {
            column = columnAttr;
          }

          if (!column && !isEmpty(columnAttr.childColumns)) {
            columnAttr.childColumns.map((childAttr) => {
              if (this.getColumnKey(childAttr) === myKey) {
                column = childAttr;
              }
            });
          }
        });
      }
      /* eslint-enable array-callback-return */
      return column;
    },
    /**
     * 컬럼의 prop 속성으로 컬럼 객체를 찾는다.
     * @author Ju
     * @param  {String} key 컬럼 prop
     * @return {Object}     컬럼 객체
     */
    findColumnByProp(key, searchable) {
      if (!key) {
        return null;
      }
      let column;
      /* eslint-disable array-callback-return */
      if (searchable) {
        this.columns.map((columnAttr) => {
          if ((columnAttr.searchable || !isEmpty(columnAttr.filters)) && columnAttr.prop === key) {
            column = columnAttr;
          }

          if (!column && !isEmpty(columnAttr.childColumns)) {
            columnAttr.childColumns.map((childAttr) => {
              if ((childAttr.searchable || !isEmpty(childAttr.filters)) && childAttr.prop === key) {
                column = childAttr;
              }
            });
          }
        });
      } else {
        this.columns.map((columnAttr) => {
          if (columnAttr.prop === key) {
            column = columnAttr;
          }

          if (!column && !isEmpty(columnAttr.childColumns)) {
            columnAttr.childColumns.map((childAttr) => {
              if (childAttr.prop === key) {
                column = childAttr;
              }
            });
          }
        });
      }
      /* eslint-enable array-callback-return */
      return column;
    },
    /**
     * 컬럼 키(컬럼의 prop속성)를 반환한다.
     * @author Ju
     * @param  {Object} columnAttr 컬럼 속성
     * @param  {?} index (호출부에서 해당 파라미터 전달하는 부분 없음)
     * @return {String} 컬럼 키
     */
    getColumnKey(columnAttr, index) {
      return columnAttr.columnKey || columnAttr.prop || index;
    },
    isSortColumn(columnAttr) {
      const sortColumn = this.states.sortColumn;
      if (!columnAttr || !sortColumn) {
        return false;
      }
      return this.getColumnKey(sortColumn) === this.getColumnKey(columnAttr);
    },
    isFilterMultiple(columnAttr) {
      /* eslint-disable no-prototype-builtins */
      return columnAttr.hasOwnProperty('filterMultiple') ? columnAttr.filterMultiple : defaultFilterMultiple;
      /* eslint-enable no-prototype-builtins */
    },
    /**
     * [EDIT] 편집 가능한 컬럼의 property 배열을 반환한다.
     * @author Ju
     * @return {Array}
     */
    getEditableColumnProps() {
      const editableColumnProps = [];
      this.columns.forEach((column) => {
        if (column.editable && column.prop) {
          editableColumnProps.push(column);
        }
      });
      return editableColumnProps;
    },
    // getEditableTableData(data) {
    //   let tableData;
    //   if (this.editable && !isEmpty(data)) {
    //     tableData = cloneDeep(data);
    //     const editableColumns = this.getEditableColumnProps();
    //     if (!isEmpty(editableColumns)) {
    //       forEach(tableData, (item) => {
    //         if (!has(item, 'modified')) {
    //           const modified = {};
    //           forEach(editableColumns, (column) => {
    //             modified[column] = undefined;
    //           });
    //           item.modified = modified;
    //         }
    //         item.editing = false;
    //       });
    //     }
    //   } else {
    //     tableData = data;
    //   }
    //   return tableData;
    // },
    /**
     * Cell 클래스명을 반환한다.
     * @author Ju
     * @param  {[type]} row         [description]
     * @param  {[type]} column      [description]
     * @param  {[type]} rowIndex    [description]
     * @param  {[type]} columnIndex [description]
     * @return {String}             Cell 클래스명
     */
    getCellClassName({ row, column, rowIndex, columnIndex }) {
      const classes = [];
      // cell-class-name
      if (this.cellClassName) {
        if (typeof this.cellClassName === 'string') {
          classes.push(this.cellClassName);
        } else if (typeof this.cellClassName === 'function') {
          classes.push(this.cellClassName({
            rowIndex,
            columnIndex,
            row,
            column,
          }));
        }
      }
      // editing
      const columnAttr = this.findColumnByProp(column.property);
      if (this.editable && columnAttr && columnAttr.editable) {
        classes.push('c-dblclick');
        if (this.isModifiedCell(row, column)) {
          classes.push('dirty');
        }
      }
      return classes.join(' ');
    },
    /**
     * 데이터가 변경된 Cell인지 판단한다.
     * true: 변경, false: 미변경
     * @author Ju
     * @param  {[type]}  row    [description]
     * @param  {[type]}  column [description]
     * @return {Boolean}        [description]
     */
    isModifiedCell(row, column) {
      return has(row.modified, column.property) && row.modified[column.property] !== undefined;
    },
    /* eslint-disable no-unused-vars, no-shadow */
    /**
     * 컬럼 헤더를 렌더링 한다.
     * @author Ju
     * @param  {Object} columnAttr 컬럼 속성
     * @return {Element}            컬럼 헤더 Element
     */
    getRenderHeaderFn(columnAttr) {
      const that = this;
      /* eslint-disable */
      const headerAlign = columnAttr.headerAlign || columnAttr.align || '';

      /* eslint-enable */
      // TODO: sort classname should be here with table-header-title
      /* eslint-disable func-names, indent, space-before-function-paren */
      return function(h, { column, $index }) {
        const hasChildren = columnAttr && !isEmpty(columnAttr.childColumns);
        // 하위 컬럼이 있는 경우 table-header-content Element를 생성하지 않는다.
        const tableContent = hasChildren ? '' : (
          <div class={['table-header-content', { hidden: !that.showSearchTool }]} on-click={(e) => that.onHeaderContentClick(e)}>
            {that.renderHeaderContent(h, columnAttr, column, $index)}
          </div>
        );
        // TODO Ju column 속성에 'renderCustomHeader'에 대한 처리 추가. 자율화 운영 고도화에서 필요한 기능을 충족하도록만 개발. 추후 다른 곳에서 사용이 필요할 경우 수정 필요.
        return (
          <div class="table-header">
            <div
              class={['table-header-title', headerAlign, { hidden: !that.showSearchTool }, { 'no-border': hasChildren }]}
              on-click={(e) =>
                that.onHeaderTitleClick(e, {
                  columnAttr,
                  column,
                })
              }
            >
              {columnAttr.editable ? (<span class="editable-header">{columnAttr.label}</span>) : (<span>{columnAttr.label}</span>)}
              {columnAttr.renderCustomHeader && columnAttr.renderCustomHeader(h, { column })}
              {columnAttr.sortable && (
                <span class="sort-caret-wrapper">
                    <i
                      class="sort-caret ascending"
                      on-click={($event) =>
                        that.handlerColumnSort($event, {
                          column,
                          columnAttr,
                        })
                      }
                    />
                    <i
                      class="sort-caret descending"
                      on-click={($event) =>
                        that.handlerColumnSort($event, {
                          column,
                          columnAttr,
                        })
                      }
                    />
                </span>
              )}
            </div>
            {tableContent}
          </div>
        );
        /* eslint-ensable func-names, indent */
      };
    },

    /**
     * 현재 설정된 검색 조건 중 값이 있는 검색 조건만 filter에 설정하여 filter 객체를 반환한다.
     * @author Ju
     * @return {Object} 필터 객체
     */
    getSearchFilters() {
      // NOTE: custom add
      const states = this.states;
      const searchFilters = {};
      Object.keys(states.searches).forEach((searchKey) => {
        const searchValue = states.searches[searchKey];
        if (!isEmpty(searchValue)) {
          searchFilters[searchKey] = [searchValue];
        }
      });
      return searchFilters;
    },
    /**
     * 현재 states의 filters 중 현재 빈 값이 설정된 filter를 제외한 filter 객체를 반환한다.
     * @author Ju
     * @return {Object} 필터 객체
     */
    getFiltersExceptEmpty() {
      const filters = cloneDeep(this.states.filters);
      const deleteKeys = [];
      Object.keys(filters).forEach((key) => {
        const col = this.findColumn(key, true);
        if (!col || col.searchType === searchType.EVENT_LEVEL || col.searchType === searchType.EVENT_LEVEL_MULTI || !isEmpty(col.filters)) {
          return;
        }
        const value = this.states.searches[key];
        if (isEmpty(value)) {
          deleteKeys.push(key);
        }
      });
      if (!isEmpty(deleteKeys)) {
        forEach(deleteKeys, (key) => {
          delete filters[key];
        });
      }
      return filters;
    },
    /**
     * 날짜 조건 필터
     * @author Ju
     * @param  {[type]} record      [description]
     * @param  {[type]} prop        [description]
     * @param  {[type]} filterValue [description]
     * @return {[type]}             [description]
     */
    filterDate(record, prop, filterValue) {
      const date = getDate(record[prop]);
      return date !== null && (date >= filterValue.start && date <= filterValue.end);
    },
    /**
     * 검색 필터를 전달된 필터로 변경한다.
     * @author Ju
     * @param  {[type]} filters [description]
     * @return {[type]}         [description]
     */
    changeSearchFilter(filters, dateFilters) {
      if (!isNil(filters) || !isNil(dateFilters)) {
        this.states = {
          ...this.states,
          ...{
            filters: isNil(filters) ? {} : filters,
            dateFilters: isNil(dateFilters) ? {} : dateFilters,
          },
        };
      }
      if (this.hasPagination()) {
        // Controlled current prop will not respond user interaction
        if (!(this.pagination && typeof this.pagination === 'object' && 'currentPage' in this.pagination)) {
          const currentPage = 1;
          this.onPageCurrentChange(currentPage);
        }
      } else {
        this.emitConditionChange();
      }
    },
    /**
     * 검색 조건, 정렬, 페이징 관련 파라미터를 생성하여 반환한다.
     * @author Ju
     * @return {[type]} [description]
     */
    getParameter() {
      const states = this.states;

      // Search Keyword
      // 하나의 필드당 하나의 키워드만 설정하는다는 전제로 구현.
      const searchParams = [];
      if (states.filters) {
        Object.keys(states.filters).forEach((columnKey) => {
          const col = this.findColumn(columnKey, true);
          if (!col || col.searchType === searchType.DATE) {
            return;
          }
          const values = states.filters[columnKey] || [];
          if (values.length === 0 || (typeof values[0] !== 'number' && isEmpty(values[0]))) {
            return;
          }
          const value = values[0];

          if (col.searchType === searchType.EVENT_LEVEL || col.filters) {
            searchParams.push({
              field: isEmpty(col.remoteProp) ? col.prop : col.remoteProp,
              value,
            });
          } else if (col.searchType === searchType.EVENT_LEVEL_MULTI) {
            /**
             * 2019. 11. 06. (SK) 다중 이벤트 등급 검색 필터링 기능 요청
             * searchType이 eventlevelMulti의 경우만 제공하며 value에 담긴 모든 값을 전달하도록
             * @author Song
             */
            searchParams.push({
              field: isEmpty(col.remoteProp) ? col.prop : col.remoteProp,
              value: values,
            });
          } else {
            searchParams.push({
              field: isEmpty(col.remoteProp) ? col.prop : col.remoteProp,
              value: isEmpty(col.filters) ? value.toString().toLowerCase() : value,
            });
          }
        });
      }

      // Date
      const dateParams = [];
      if (states.dateFilters) {
        Object.keys(states.dateFilters).forEach((columnKey) => {
          const col = this.findColumn(columnKey, true);
          if (!col || col.searchType !== searchType.DATE) {
            return;
          }
          const filterValue = states.dateFilters[columnKey] || [];
          if (isEmpty(filterValue)) {
            return;
          }
          dateParams.push({
            field: isEmpty(col.remoteProp) ? col.prop : col.remoteProp,
            start: getDateToString(filterValue.start, 'YYYY-MM-DD 00:00:00'),
            end: getDateToString(filterValue.end, 'YYYY-MM-DD 23:59:59'),
          });
        });
      }

      let dir;
      if (isEmpty(states.sortOrder)) {
        dir = null;
      } else {
        dir = states.sortOrder === 'ascending' ? 'ASC' : 'DESC';
      }

      let sort;
      if (isEmpty(states.sortColumn)) {
        sort = null;
      } else {
        const sortProp = states.sortColumn.sortProp;
        if (!isEmpty(sortProp)) {
          sort = sortProp;
        } else {
          sort = isEmpty(states.sortColumn.remoteProp) ? states.sortColumn.prop : states.sortColumn.remoteProp;
        }
      }

      let params = {
        searchParams: isEmpty(searchParams) ? null : searchParams,
        dateSearchParams: isEmpty(dateParams) ? null : dateParams,
        sort,
        dir,
      };

      if (this.hasPagination()) {
        const pagination = states.pagination;
        const pageParams = {
          start: (pagination.currentPage - 1) * pagination.pageSize,
          limit: pagination.pageSize,
        };

        params = {
          ...params,
          ...pageParams,
        };
      }

      return params;
    },
    /**
     * 조건(검색, 정렬, 페이징) 변경 이벤트를 발신한다.
     * @author Ju
     * @return {[type]} [description]
     */
    emitConditionChange() {
      const params = this.getParameter();
      this.$emit('condition-change', params);
    },
    getTable() {
      return this.$refs['ll-table'];
    },
    getPagination() {
      return {
        ...this.states.pagination,
      };
    },
    setCurrentPage(currentPage) {
      this.onPageCurrentChange(currentPage);
    },
    /**
     * 필터 설정을 초기화한다.
     * @author Ju
     * @return {[type]} [description]
     */
    clearFilter() {
      const states = this.states;

      if (!isEmpty(this.states.sortElTableColumn)) {
        this.states.sortElTableColumn.order = undefined;
      }

      this.states = {
        filters: {},
        dateFilters: {},
        sortElTableColumn: null,
        sortColumn: null,
        sortOrder: null,
        searches: {},
        pagination: {
          ...states.pagination,
          currentPage: 1,
        },
      };
    },
  },
  /**
   * 컴포넌트를 렌더링한다.
   * @return  {Element} 테이블 Element
   * @author Ju
   */
  render() {
    const that = this;
    const tableOptions = {
      showCustomHeader: this.showCustomHeader,
      data: this.currentPageData,
      defaultSort: this.defaultSort,
    };
    const defaultColumnOptions = this.columnDefault || {};

    const props = {
      ...tableOptions,
      ...this.$attrs,
    };

    if (this.fitHeight) {
      if (this.hasPagination() && !isEmpty(this.currentPageData)) {
        props.height = 'calc(100% - 44px)';
      } else {
        props.height = '100%';
      }
    }

    props.cellClassName = this.getCellClassName;

    return (
      <div class="ll-table-container">
        <el-table
          class={{ 'll-table': true, 'custom-header': this.showCustomHeader, [this.type]: true }}
          ref="ll-table"
          {...{
            props,
            on: {
              ...this.$listeners,
              'filter-change': that.onTableFilterChange,
              'sort-change': that.onTableSortChange,
            },
            slot: 'append',
          }}
        >
          {this.$slots.append && (
            <div class="ll-table-body-append" slot="append">
              {this.$slots.append}
            </div>
          )}
          {this.columns.map((column) => {
            const columnOptions = {
              ...defaultColumnOptions,
              ...column,
            };
            return this.renderColumn(columnOptions, tableOptions);
          })}
        </el-table>
        <el-row type="flex" justify="center">
          {this.renderPagination()}
        </el-row>
      </div>
    );
  },
};
</script>

<style lang="scss">
.ll-table-container {
  $bordor-bottom-line-color: #e6ebf5;

  .ll-table-pagination {
    margin-top: 16px;
    padding: 0;
    //display: flex;
    justify-content: flex-end;
  }

  .ll-table.custom-header {
    th {
      // &.is-sortable {
        padding: 2px 0 0 0;
          & > .cell {
            padding: 3px 0 0 6px !important; //0429 수정
      }
      //
      //  padding: 0 0;
      div {
        padding: 0 0;
        line-height: 23px;
      }
    }
    th .cell {
      vertical-align: top;
      line-height: 14px;
      .caret-wrapper {
        display: none;
      }
      .el-table__column-filter-trigger {
        display: none;
      }
      .editable-icon {
        font-size: 10px;
        color: #999;
        margin: 0 0 2px 4px;
      }
    }
    th.el-table-column--selection .cell {
      padding-left: 14px !important;
      border-bottom: none;
      // padding: 12px 10px;
      // line-height: 23px;
      // border-bottom: 1px solid $bordor-bottom-line-color;
    }
    th.ll-index .cell {
      padding: 12px 10px;
      line-height: 23px;
      // border-bottom: 1px solid $bordor-bottom-line-color;
    }
    .table-header {
      width: 100%;
      .editable-header {
        color: #3176a0;
      }
    }


    .is-sortable .table-header-title {
       padding: 3px 0px 2px 0px;
    }

    .table-header-title {
      box-sizing: border-box;
      //edit 1130 ie
      padding: 4px 0px 4px 0px;
      line-height: 15px;
      min-height: 20px;
      //end 1130
      // border-bottom: 1px solid $bordor-bottom-line-color;

      display: flex;
      justify-content: flex-start;
      align-items: center;

      &.left {
        justify-content: flex-start;
      }
      &.right {
        justify-content: flex-end;
      }
      &.center {
        justify-content: center;
      }

      &.hidden {
        /* NOTE: cusotm */
        border-bottom: none;
      }

      &.no-border {
        border-bottom: none;
      }

      .sort-caret-wrapper {
        display: inline-flex;
        flex-direction: column;
        align-items: center;
        height: 19px;
        width: 24px;
        vertical-align: middle;
        cursor: pointer;
        overflow: visible;
        overflow: initial;
        position: relative;

        .sort-caret.ascending {
          top: 0px;
        }
        .sort-caret.descending {
          bottom: 1px;
        }
        // cursor: pointer;
        // position: relative;
        // display: inline-flex;
        // align-items: center;
        // width: 24px;
        // height: 13px;
        // line-height: 23px;
        // overflow: initial;
        //
        // .sort-icon-wrapper {
        //   color: #878d99;
        //   width: 14px;
        //   overflow: hidden;
        //   font-size: 13px;
        //   line-height: 23px;
        // }
      }
    }

    // .ascending .sort-icon-wrapper .el-icon-sort-up {
    //   color: #409eff;
    // }
    // .descending .sort-icon-wrapper .el-icon-sort-down {
    //   color: #409eff;
    // }
    //
    .is-sortable .table-header-content {
      margin-top: 0px !important;
    }

    .table-header-content {
      margin-top: 3px;

      box-sizing: border-box;
      padding: 10px 10px 10px 0;
      line-height: 23px;
      cursor: default;

      display: flex;
      justify-content: center;
      align-items: center;

      $height: 22px;

      &.hidden {
        /* NOTE: cusotm */
        display: none !important;
      }

      &.no-border {
        border-bottom: none;
      }

      .icon-search-delete {
        cursor: pointer;
        // line-height: 23px;
      }
      .el-select {
        width: 100%;
        font-size: 12px;

        .el-input {
          input {
            height: 26px;
            max-height: 26px;
            overflow: hidden;
          }
        }

        .el-select__tags {
          background-color: transparent;
          height: $height;
          line-height: $height;
          max-height: $height;
          overflow: hidden;
        }

        .el-tag {
          height: $height;
          line-height: $height;
          margin: 0 0 0 6px;
        }
      }

      .el-input__inner {
        height: 26px;
      }
      .el-date-editor--daterange.el-input, .el-date-editor--daterange.el-input__inner {
        width: 100%;
      }

      .el-date-editor .el-input__icon.el-range__icon.el-icon-date {
        margin-left: 4px;
      }

      .el-input {
        width: 100%;
        font-size: 12px;

        .el-input__suffix-inner .el-input__icon {
          width: 18px;
        }
      }
      .el-input--suffix .el-input__inner {
        padding: 0 7px 0 7px !important;
      }
    }
  }
  .el-table__row .dirty::before {
    content: '';
    width: 0;
    height: 0;
    border-top: 0 solid transparent;
    border-left: 7px solid #f56c6c;
    border-bottom: 7px solid transparent;
    left: 0;
    top: 0;
    position: absolute;
    clear: both;
  }

  .el-table--border.ll-table.custom-header th.el-table-column--selection .cell {
    padding-left: 10px !important;
  }
}
.el-select-dropdown.el-popper.is-multiple.eventlevel-combo {
  width: 120px !important;
  .el-select-dropdown__item.selected::after {
    color: #ffff !important;
  }
}

</style>



// WEBPACK FOOTER //
// src/components/external/el-table-wrapper/components/ElTableWrapper.vue